import { useState } from 'react';
import Button, { Color } from 'lib/Button';
import checkAllocationSvg from 'assets/icons/check-alloc.svg';
import Header from 'components/common/Header';
import { PageWrapper } from 'components/common/layout/PageWrapper/PageWrapper';
import Preloader from 'lib/Preloader';
import useChannels from 'customHooks/useChannels';
import { AvailabilityReportFilter } from 'components/pages/AvailabilityReport/AvailabilityReport.types';
import { CircularLoadingSpinner } from 'lib/CircularLoadingSpinner/CircularLoadingSpinner';
import AvailabilityReportFilters from './AvailabilityReportFilters';
import { AvailabilityReportTable } from './AvailabilityReportTable';
import { useAvailabilityReport } from './useAvailabilityReport';
import { hasMandatoryFilters } from './utils';

const hasAvailabilityRowsToExport = (shouldShowTable: boolean, isTableLoading: boolean, rowCount: number): boolean =>
  shouldShowTable && !isTableLoading && rowCount > 0;

export const AvailabilityReport: React.FC = () => {
  const [selectedFilters, setSelectedFilters] = useState<AvailabilityReportFilter>({});
  const [hasClickedCheckAvailability, setHasClickedCheckAvailability] = useState(false);
  const [hasData, setHasData] = useState(false);

  useChannels();

  const {
    availabilityReportTableParams: { gridOptions, onGridReady, rowData, pinnedBottomRowData },
    handleChangeMetricType,
    handleFetchAvailabilityReport,
    clearAvailabilityReportData,
    exportGridAsCsv,
    exportGridAsExcel,
    handleExportReport,
    isLoading,
    isTableLoading,
    isExportLoading,
    selectedMetricType,
    selectedRowGroupColumns,
    handleToggleRowGroupColumn,
    resetRowGroupColumns,
    rowGroupColumns,
  } = useAvailabilityReport({
    onDataFetched: () => setHasData(true),
  });

  const updateFilters = (val: Partial<AvailabilityReportFilter>): void => {
    setSelectedFilters((prev) => ({ ...prev, ...val }));
  };

  const clearFilters = (): void => {
    setSelectedFilters({});
    resetRowGroupColumns();
    clearAvailabilityReportData();
    setHasClickedCheckAvailability(false);
    setHasData(false);
  };

  const isDisabled = !hasMandatoryFilters(selectedFilters);

  const handleCheckAvailability = (): void => {
    if (!isDisabled) {
      setHasClickedCheckAvailability(true);
      handleFetchAvailabilityReport(selectedFilters);
    }
  };

  const shouldShowTable = (hasClickedCheckAvailability || hasData) && !isDisabled;
  const isViewExportDisabled = !hasAvailabilityRowsToExport(shouldShowTable, isTableLoading, rowData.length);

  const handleExport = (): void => {
    if (!isDisabled) {
      handleExportReport(selectedFilters);
    }
  };

  return (
    <PageWrapper>
      <Header title="Availability report" />
      <div className="flex flex-col h-full px-14 pt-4">
        <div className="flex justify-end">
          <Button
            color={Color.PRIMARY}
            classNames="p-2 pointer-events-auto"
            label="Check availability"
            svg={checkAllocationSvg}
            onClick={handleCheckAvailability}
            isDisabled={isDisabled}
          />
        </div>

        <AvailabilityReportFilters
          selectedFilters={selectedFilters}
          updateFilters={updateFilters}
          clearFilters={clearFilters}
          selectedRowGroupColumns={selectedRowGroupColumns}
          handleToggleRowGroupColumn={handleToggleRowGroupColumn}
          rowGroupColumns={rowGroupColumns}
          isDisabled={isDisabled}
          selectedMetricType={selectedMetricType}
          handleChangeMetricType={handleChangeMetricType}
          exportGridAsCsv={exportGridAsCsv}
          exportGridAsExcel={exportGridAsExcel}
          exportAllAsCsv={handleExport}
          isViewExportDisabled={isViewExportDisabled}
          isExportLoading={isExportLoading}
        />

        <div className="flex-1 relative">
          {isLoading ? (
            <Preloader isLoading={isLoading} />
          ) : (
            <>
              <div className={`h-full ${isTableLoading ? 'pointer-events-none opacity-70 cursor-wait' : ''}`}>
                <AvailabilityReportTable
                  gridOptions={gridOptions}
                  rowData={shouldShowTable ? rowData : []}
                  columnDefs={gridOptions.columnDefs || []}
                  pinnedBottomRowData={shouldShowTable ? pinnedBottomRowData : undefined}
                  onGridReady={onGridReady}
                />
              </div>
              {isTableLoading && (
                <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                  <CircularLoadingSpinner />
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </PageWrapper>
  );
};

export default AvailabilityReport;
